import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useCallback,
} from "react";
import WebEditorMenuBar from "../../screens/WebEditor/MenuBar";
import SelectableTextOverlapSideBar from "../../screens/WebEditor/Design/SelectableTextSideBar/selectableTextOverlapSideBar";
import SelectableTextSideBar from "../../screens/WebEditor/Design/SelectableTextSideBar/selectableTextSideBar";
import TextLibraryOverlapSideBar from "../../screens/WebEditor/Design/TextLibrarySideBar/textLibraryOverlapSideBar";
import TextLibrarySideBar from "../../screens/WebEditor/Design/TextLibrarySideBar/textLibrarySideBar";
import DistributionDialog from "../../screens/WebEditor/Design/DistributionDialog";
import DiscardDialog from "../../screens/WebEditor/Design/DiscardDialog";
import TextLibraryDialog from "../../screens/WebEditor/Design/TextLibraryDialog";
import {
  WebEditorContext,
  SelectableTextContext,
  TextLibraryContext,
  DistributionContext,
  DiscardContext,
  MetaforceContext,
} from "../../contexts";
import * as Constants from "../../utils/constants";
import { useToastAction } from "../../hooks/useToastAction";
import { getEditorFile, updateEditorFile, getAttachmentByStartParameter, previewPdf, previewHtml, getViewpointPdf, getDynamoPdf, getProcesses, reloadSmartform, getDistributionList } from "../../api/webEditor";
import { Formik } from "formik";
import * as yup from "yup";
import _ from "lodash";
import { useEditorFile } from "../../hooks/useEditorFile";
import { Transition } from "@headlessui/react";
import { Fragment } from "react";
import { useAutoSave } from "../../hooks/useAutoSave";
import { useQuill } from "../../hooks/useQuill";
import { useUndoRedo } from "../../hooks/useUndoRedo";
import {
  resources,
  translations,
} from "../../translations/translationsConstants";
import { useTranslation } from "react-i18next";
import i18n from "../../translations/i18n";
import { v4 as uuidv4 } from "uuid";
import {
  convertToi18nLanguage,
  setAcceptLanguageHeader,
  setAuthHeader,
} from "../../api";
import GenericModal from "../../components/Modals/genericModal";
import {
  authenticatePkce,
  clearToken,
  getTokenFromStorage,
  verifyToken,
} from "../../utils/oauthAuthentication";
import { useHotkeys } from "react-hotkeys-hook";
import MandatoryFieldBanner from "../../screens/WebEditor/Design/MandatoryFieldBanner";
import { config } from "../../utils/config";
import PdfPreviewModal from "../../components/Modals/PdfPreviewModal";
import LoadingModal from "../../components/Modals/LoadingModal";
import "./styles.scss";
import PreviewView from "./PreviewView";
import SectionHeader from "./SectionHeader";
import SplitView from "../../components/SplitView";
import RightClickMenuModal from "../../components/Modals/menuModal";

// import './main.css';

export function BaseLayout({ nav, children }) {
  const {
    startParameter,
    discard,
    publish,
    themeAndStyle,
    toastHandler,
    successAfterFailedToSaveHandler,
    jwt,
    onOneTimeJwtUsed,
  } = useContext(MetaforceContext);
  const { findEditorInsertPoint, getSelectableTexts, detectLanguageCode } =
    useEditorFile();
  const editorFileRef = useRef({});
  const [editorAttachment, setEditorAttachment] = useState();
  const [saveHasFailed, setSaveHasFailed] = useState(false);
  const attachmentsToDeleteRef = useRef([]);
  const editorAttachmentRef = useRef();
  editorAttachmentRef.current = editorAttachment;
  const [modifiedDate, setModifiedDate] = useState();
  const [currentQuillId, setCurrentQuillId] = useState();
  const [searchClickedCount, setSearchClickedCount] = useState(0);
  const { getQuill, insertQuillText } = useQuill();
  const quillRef = useRef();
  const undoRedoManager = useUndoRedo(5);

  // Helper function to update undo/redo button states
  const updateUndoRedoButtonStates = useCallback(() => {
    setActionMenuBar((prev) => ({
      ...prev,
      undoState:
        undoRedoManager.canUndo && !undoRedoLoading
          ? Constants.normalState
          : Constants.disabledState,
      redoState:
        undoRedoManager.canRedo && !undoRedoLoading
          ? Constants.normalState
          : Constants.disabledState,
    }));
  }, [undoRedoManager.canUndo, undoRedoManager.canRedo, undoRedoLoading]);
  const lastUsedQuillRef = useRef();
  const lastUsedQuillIndexRef = useRef();
  const [lastUsedQuillId, setLastUsedQuillId] = useState();
  const [editorDisabled, setEditorDisabled] = useState(false);
  const [showBackButton, setShowBackButton] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewType, setPreviewType] = useState(Constants.previewType.pdf);
  const [previewRefreshDisabled, setPreviewRefreshDisabled] = useState(true);
  const [forcePreviewLoad, setForcePreviewLoad] = useState(0);
  const [rightClickMenu, setRightClickMenu] = useState(false);

  const [formatMenuBar, setFormatMenuBar] = useState({
    boldButtonState: Constants.formatButtonState.disabledAndUnfocused,
    italicButtonState: Constants.formatButtonState.disabledAndUnfocused,
    underlineButtonState: Constants.formatButtonState.disabledAndUnfocused,
    alignLeftButtonState: Constants.formatButtonState.disabledAndUnfocused,
    alignRightButtonState: Constants.formatButtonState.disabledAndUnfocused,
    alignCenterButtonState: Constants.formatButtonState.disabledAndUnfocused,
    alignJustifyButtonState: Constants.formatButtonState.disabledAndUnfocused,
    listButtonState: Constants.formatButtonState.disabledAndUnfocused,
    numberedListButtonState: Constants.formatButtonState.disabledAndUnfocused,
    fontSizeButtonState: Constants.formatButtonState.disabledAndUnfocused,
    fontSizeButtonValue: 10,
    fontFamilyButtonState: Constants.formatButtonState.disabledAndUnfocused,
    fontFamilyButtonText: "",
  });
  const [actionMenuBar, setActionMenuBar] = useState({
    selectableTextState: Constants.normalState,
    textLibraryState: Constants.normalState,
    previewPdfState: Constants.normalState,
    previewHtmlState: Constants.normalState,
    splitScreenState: Constants.screenState.singleScreen,
    publishState: Constants.normalState,
    discardState: Constants.normalState,
    undoState: Constants.disabledState,
    redoState: Constants.disabledState,
  });
  const { t: tc } = useTranslation([resources.common]);
  const { t: te } = useTranslation([resources.edit]);
  const { t: terr } = useTranslation([resources.errors]);

  const handleTextLibraryOpen = () => {
    if (!editorFileRef?.current?.WebEditorSettings?.TextLibraryEnabled) return;
    setSideBarState(Constants.sideBarState.textLibrary);
    setSearchClickedCount((prev) => prev + 1);
  };

  useHotkeys("ctrl+f", () => handleTextLibraryOpen(), []);
  useHotkeys("ctrl+e", () => lastUsedQuillRef?.current.focus(), []);

  const handleActionMenuBar = (args) => {
    const newActionMenuBar = _.cloneDeep(actionMenuBar);
    if (args.actionMenuBar) {
      Object.entries(args.actionMenuBar).forEach((entry) => {
        const [key, value] = entry;
        newActionMenuBar[key] = value;
      });
    }

    if (nav === Constants.menuState.attachment) {
      newActionMenuBar.selectableTextState = Constants.disabledState;
      newActionMenuBar.textLibraryState = Constants.disabledState;
    }

    if (args.editorFile) {
      if (args.editorFile.DxmlFileStatus === Constants.fileStatus.draft) {
        if (!args.editorFile.HasSelectableText) {
          newActionMenuBar.selectableTextState = Constants.disabledState;
        }
        if (!args.editorFile.WebEditorSettings.TextLibraryEnabled) {
          newActionMenuBar.textLibraryState = Constants.disabledState;
        }
        if (!args.editorFile.WebEditorSettings.PreviewHtmlEnabled) {
          newActionMenuBar.previewHtmlState = Constants.disabledState;
        }
      } else {
        newActionMenuBar.selectableTextState = Constants.disabledState;
        newActionMenuBar.textLibraryState = Constants.disabledState;
        newActionMenuBar.previewPdfState = Constants.disabledState;
        newActionMenuBar.previewHtmlState = Constants.disabledState;
        newActionMenuBar.splitScreenState = Constants.disabledState;
        newActionMenuBar.publishState = Constants.disabledState;
        newActionMenuBar.discardState = Constants.disabledState;
      }
    }

    setActionMenuBar((prev) => ({
      ...newActionMenuBar,
      undoState: prev.undoState,
      redoState: prev.redoState,
    }));
  };

  const [sideBarState, setSideBarState] = useState(
    Constants.sideBarState.closed,
  );
  const [selectableTexts, setSelectableTexts] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const selectedTextPartIdRef = useRef();
  const [hoveredSelectableText, setHoveredSelectableText] = useState();

  const [selectedEditorInsertPoint, setSelectedEditorInsertPoint] = useState();
  const [textLibraryDialogOpen, setTextLibraryDialogOpen] = useState(false);
  const [
    quillBeforeTextLibraryDialogOpen,
    setQuillBeforeTextLibraryDialogOpen,
  ] = useState();
  const [
    templateTextValueSelectedInDialog,
    setTemplateTextValueSelectedInDialog,
  ] = useState();

  const [distributionDialogOpen, setDistributionDialogOpen] = useState(false);
  const [distributionOptions, setDistributionOptions] = useState([]);

  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);

  const [formikInitialValues, setFormikInitialValues] = useState({});
  const formikValidationSchema = useRef({});
  const formikValuesRef = useRef({});
  const formikMethodsRef = useRef({});

  const inputFieldTouchedRef = useRef({});

  const loadAction = useToastAction();
  const authAction = useToastAction();
  const [loginRequiredModalOpen, setLoginRequiredModalOpen] = useState(false);
  const [getEditorFailedModalOpen, setGetEditorFailedModalOpen] =
    useState(false);
  const [editorFailedModalData, setEditorFailedModalData] = useState(null);
  const [authConfig, setAuthConfig] = useState(null);
  const [pdfPreviewModal, setPdfPreviewModal] = useState({
    open: false,
    blobUrl: null,
    title: "",
  });
  const [localPrintLoading, setLocalPrintLoading] = useState(false);
  const [undoRedoLoading, setUndoRedoLoading] = useState(false);
  const [sessionId] = useState(() => uuidv4());

  const [languageCodeFromQueryString, setLanguageCodeFromQueryString] =
    useState();
  const [currentLanguage, setCurrentLanguage] = useState("en");
  const changeLanguageCode = (editorFile) => {
    let { languageCode, lngFromQueryString } = detectLanguageCode(editorFile);

    setLanguageCodeFromQueryString(lngFromQueryString);
    const i18Lang = convertToi18nLanguage(languageCode);
    setCurrentLanguage(editorFile.CultureInfo || i18Lang);
    setAcceptLanguageHeader(i18Lang);
    i18n.changeLanguage(languageCode);
  };

  const getEditorFileErrorHandlers = [
    {
      cancelThrow: true,
      statusCode: 401,
      handler: async (data) => {
        handleUnauthorized(data);
      },
    },
    {
      // cancelThrow: true,
      statusCode: 403,
      handler: async (data) => {
        handleForbidden(data);
      },
    },
  ];

  const loadData = async () => {
    setEditorFailedModalData(null);
    // if (jwt) {
    //     setAuthHeader(jwt);
    // }

    loadAction.execute(async () => {
      editorFileRef.current = {};
      if (sessionStorage.getItem("document-token") && !jwt) {
        setAuthHeader(sessionStorage.getItem("document-token"));
      }
      const newEditorFile = await getEditorFile(
        startParameter,
        getEditorFileErrorHandlers,
        jwt,
      );
      if (jwt) {
        onOneTimeJwtUsed();
      }
      if (!newEditorFile) return;

      // load Smartform
      setShowBackButton(newEditorFile.ShowBackButton);
      if (newEditorFile.SmartformRedirectUrl) {
        window.location.href = newEditorFile.SmartformRedirectUrl;
        return;
      }

      const newAttachmentDto =
        await getAttachmentByStartParameter(startParameter);

      changeLanguageCode(newEditorFile);

      editorFileRef.current = newEditorFile;

      // Save initial state for undo/redo
      if (undoRedoManager && newEditorFile) {
        undoRedoManager.saveState({
          editorFile: _.cloneDeep(newEditorFile),
          timestamp: Date.now(),
          source: 'loadData'
        });
      }

      // Initialize undo/redo button states after file load
      updateUndoRedoButtonStates();

      handleData({
        editorFile: editorFileRef.current,
        actionMenuBar: {
          selectableTextState: Constants.normalState,
          textLibraryState: Constants.normalState,
          previewPdfState: Constants.normalState,
          previewHtmlState: Constants.normalState,
          splitScreenState: Constants.screenState.singleScreen,
          publishState: Constants.normalState,
          discardState: Constants.normalState,
        },
      });
      setEditorAttachment(newAttachmentDto);
      setModifiedDate(newEditorFile.ModifiedTime);
      setEditorDisabled(
        newEditorFile.DxmlFileStatus !== Constants.fileStatus.draft,
      );

      if (
        newEditorFile.HasSelectableText &&
        nav === Constants.menuState.edit &&
        newEditorFile.DxmlFileStatus === Constants.fileStatus.draft
      ) {
        setSideBarState(Constants.sideBarState.predefinedTexts);
      }
    }, terr(translations.errors.failLoadDxmlFile));
  };

  const handleUnauthorized = (data) => {
    if (!!data) {
      const tokenResponse = getTokenFromStorage();

      if (tokenResponse) {
        const tokenValid = verifyToken(tokenResponse);
        if (!tokenValid || data.tokenValidationFailed) {
          clearToken();
          console.log("Token is not valid or token validation failed", data);
          handleUnauthorized(data);
        } else {
          console.log("Token is valid, setting token and reloading");
          setTokenAndReload(tokenResponse);
        }
      } else {
        console.log(
          "No token found, setting auth config and opening login modal",
        );
        setAuthConfig(data);
        clearToken();
        setLoginRequiredModalOpen(true);
        changeLanguageCode({
          WebEditorSettings: { LanguageCode: data.languageCode },
        });
      }
    } else {
      setGetEditorFailedModalOpen(true);
      setEditorFailedModalData({
        statusCode: 401,
      });
    }
  };

  const handleForbidden = (data) => {
    if (!!data) {
      setGetEditorFailedModalOpen(true);
      setEditorFailedModalData({
        statusCode: 403,
        message: data.Message || terr(translations.errors.forbidden),
      });
    } else {
      setGetEditorFailedModalOpen(true);
      setEditorFailedModalData({
        statusCode: 403,
      });
    }
  };

  const handleAuthenticate = async () => {
    authAction.execute(async () => {
      const tokenResponse = await authenticatePkce(authConfig);
      setTokenAndReload(tokenResponse);
    }, "Authentication failed");
  };

  const setTokenAndReload = (tokenResponse) => {
    setAuthHeader(tokenResponse.idToken);
    setAuthConfig(null);
    //leave for now
    // console.log('accessToken', tokenResponse);
    setLoginRequiredModalOpen(false);
    loadData();
  };

  useEffect(() => {
    loadData();
  }, [startParameter]);

  useEffect(() => {
    if (nav === Constants.menuState.edit) {
      if (
        editorFileRef.current?.HasSelectableText &&
        editorFileRef.current?.DxmlFileStatus === Constants.fileStatus.draft
      ) {
        setSideBarState(Constants.sideBarState.predefinedTexts);
      }
    } else {
      setSideBarState(Constants.sideBarState.closed);
    }

    selectedTextPartIdRef.current = null;
    setSelectedEditorInsertPoint(null);

    setDistributionDialogOpen(false);
    setDiscardDialogOpen(false);

    setFormatMenuBar((preState) => ({
      ...preState,
      boldButtonState: Constants.formatButtonState.disabledAndUnfocused,
      italicButtonState: Constants.formatButtonState.disabledAndUnfocused,
      underlineButtonState: Constants.formatButtonState.disabledAndUnfocused,
      alignLeftButtonState: Constants.formatButtonState.disabledAndUnfocused,
      alignRightButtonState: Constants.formatButtonState.disabledAndUnfocused,
      alignCenterButtonState: Constants.formatButtonState.disabledAndUnfocused,
      alignJustifyButtonState: Constants.formatButtonState.disabledAndUnfocused,
      listButtonState: Constants.formatButtonState.disabledAndUnfocused,
      numberedListButtonState: Constants.formatButtonState.disabledAndUnfocused,
      fontSizeButtonState: Constants.formatButtonState.disabledAndUnfocused,
      fontFamilyButtonState: Constants.formatButtonState.disabledAndUnfocused,
    }));

    handleActionMenuBar({
      actionMenuBar: {
        selectableTextState: Constants.normalState,
        textLibraryState: Constants.normalState,
        previewPdfState: Constants.normalState,
        previewHtmlState: Constants.normalState,
        splitScreenState: Constants.screenState.singleScreen,
        publishState: Constants.normalState,
        discardState: Constants.normalState,
      },
      editorFile: _.cloneDeep(editorFileRef.current),
    });
  }, [nav]);

  useEffect(() => {
    if (!textLibraryDialogOpen) {
      if (quillBeforeTextLibraryDialogOpen) {
        const quill = getQuill(quillBeforeTextLibraryDialogOpen.id);
        if (quill) {
          if (templateTextValueSelectedInDialog) {
            // select text and close TextLibrary dialog
            const formats = quill.getFormat(
              quillBeforeTextLibraryDialogOpen.index,
              0,
            );
            quill.setSelection(quillBeforeTextLibraryDialogOpen.index, 0);
            quill.deleteText(quillBeforeTextLibraryDialogOpen.index, 2);
            insertQuillText(
              quill,
              templateTextValueSelectedInDialog.Text,
              quillBeforeTextLibraryDialogOpen.index,
            );
            quill.setSelection(
              quillBeforeTextLibraryDialogOpen.index,
              templateTextValueSelectedInDialog.Text?.length,
            );
          } else {
            // type cancel and close TextLibrary dialog
            // Firstly select text and then delete '#?' to fire Quill text-change event to autosave document
            quill.setSelection(quillBeforeTextLibraryDialogOpen.index, 0);
            quill.deleteText(quillBeforeTextLibraryDialogOpen.index, 2);
          }
        }
      }

      setQuillBeforeTextLibraryDialogOpen(null);
      setTemplateTextValueSelectedInDialog(null);
    }
  }, [textLibraryDialogOpen]);

  const isValidNumber = (valueAsString) => {
    if (!valueAsString) return true;
    // For '', '   ', ' 100 ', isNaN will return false
    if (valueAsString.indexOf(" ") >= 0 || isNaN(valueAsString)) return false;
    return true;
  };

  const buildValidationRule = (inputFormat, editorInputField) => {
    if (!inputFormat) return;

    let rule = inputFormat;

    if (
      editorInputField.InputFieldType === Constants.inputFieldType.decimal ||
      editorInputField.InputFieldType === Constants.inputFieldType.integer
    ) {
      rule = rule.test({
        name: "validNumber",
        test(value, ctx) {
          if (!isValidNumber(value)) {
            return ctx.createError({
              message: `${te(translations.edit.inputFieldValidation.invalid)}`,
            });
          }
          return true;
        },
      });
    }

    if (editorInputField.ValidationRule.NotEmpty)
      rule = rule.required(te(translations.edit.inputFieldValidation.required));

    if (editorInputField.ValidationRule.MinLength)
      rule = rule.min(
        editorInputField.ValidationRule.MinLength,
        `${te(translations.edit.inputFieldValidation.atLeast)} ${editorInputField.ValidationRule.MinLength} ${te(translations.edit.inputFieldValidation.characterRequired)}`,
      );

    if (editorInputField.ValidationRule.MaxLength)
      rule = rule.max(
        editorInputField.ValidationRule.MaxLength,
        `${te(translations.edit.inputFieldValidation.atMost)} ${editorInputField.ValidationRule.MaxLength} ${te(translations.edit.inputFieldValidation.characterRequired)}`,
      );

    return rule;
  };

  const addToSchema = (inputFormat, schema, editorInputField) => {
    if (!inputFormat) return;

    const rule = buildValidationRule(inputFormat, editorInputField);

    if (editorInputField.EditorTextPartId) {
      schema[editorInputField.Id] = inputFormat.when(
        editorInputField.EditorTextPartId,
        {
          is: (value) => value === true,
          then: rule,
        },
      );
    } else {
      schema[editorInputField.Id] = rule;
    }
  };

  const updateFormikValidationSchemaByEditorInputField = (
    schema,
    editorInputField,
  ) => {
    let inputFormat;

    switch (editorInputField.InputFieldType) {
      case Constants.inputFieldType.date:
        inputFormat = yup.date();
        break;
      case Constants.inputFieldType.decimal:
      case Constants.inputFieldType.integer:
        inputFormat = yup.string();
        break;
      case Constants.inputFieldType.text:
        inputFormat = yup.string();
        break;
      case Constants.inputFieldType.select:
        inputFormat = yup.string();
        break;
    }

    addToSchema(inputFormat, schema, editorInputField);
  };

  const handleData = (args) => {
    const newFormikInitialValues = {};

    let newSelectableTexts = getSelectableTexts(args.editorFile);
    setSelectableTexts(newSelectableTexts);

    let newKeywords = [];
    let allHasKeywords = true;

    newSelectableTexts.forEach((selectableText) => {
      if (selectableText.Type === Constants.editorType.textChoice) {
        selectableText.Items.forEach((editorTextPart) => {
          newFormikInitialValues[editorTextPart.Id] = editorTextPart.Visible;

          allHasKeywords =
            allHasKeywords && editorTextPart.Keywords?.length > 0;

          editorTextPart.Keywords?.forEach((keyword) => {
            if (!newKeywords.includes(keyword)) {
              newKeywords.push(keyword);
            }
          });
        });
      } else if (selectableText.Type === Constants.editorType.textPart) {
        newFormikInitialValues[selectableText.Id] = selectableText.Visible;

        allHasKeywords = allHasKeywords && selectableText.Keywords?.length > 0;

        selectableText?.Keywords?.forEach((keyword) => {
          if (!newKeywords.includes(keyword)) {
            newKeywords.push(keyword);
          }
        });
      }
    });

    // <Blanke> must be always added, otherwise the selectable texts in the DXML file without keywords will not be shown.

    if (!allHasKeywords) newKeywords.unshift("<Blanke>");

    setKeywords(newKeywords);

    if (selectedEditorInsertPoint) {
      const newSelectedEditorInsertPoint = findEditorInsertPoint(
        args.editorFile,
        selectedEditorInsertPoint.Id,
      );
      if (newSelectedEditorInsertPoint) {
        setSelectedEditorInsertPoint(newSelectedEditorInsertPoint);
      }
    }

    for (const key in formikValidationSchema.current) {
      delete formikValidationSchema.current[key];
    }

    args.editorFile.EditorInputFields.forEach((editorInputField) => {
      if (
        editorInputField.InputFieldType === Constants.inputFieldType.checkbox
      ) {
        newFormikInitialValues[editorInputField.Id] =
          editorInputField.Value === "true" ? true : false;
      } else if (
        editorInputField.InputFieldType === Constants.inputFieldType.date
      ) {
        // let dateValue = new Date();
        // if (!editorInputField.Value) {
        //     dateValue = new Date();
        // } else {
        //     dateValue = new Date(editorInputField.Value);
        // }
        // newFormikInitialValues[editorInputField.Id] = format(dateValue, "yyyy-MM-dd");
        newFormikInitialValues[editorInputField.Id] = editorInputField.Value;
      } else {
        newFormikInitialValues[editorInputField.Id] = editorInputField.Value;
      }

      updateFormikValidationSchemaByEditorInputField(
        formikValidationSchema.current,
        editorInputField,
        newFormikInitialValues,
      );
    });

    setFormikInitialValues(newFormikInitialValues);
    formikValuesRef.current = newFormikInitialValues;

    // Force Formik to update with new values if methods are available
    if (formikMethodsRef.current.setValues) {
      setTimeout(() => {
        formikMethodsRef.current.setValues(newFormikInitialValues);
      }, 50);
    }

    setFormatMenuBar((preState) => ({
      ...preState,
      boldButtonState: Constants.formatButtonState.disabledAndUnfocused,
      italicButtonState: Constants.formatButtonState.disabledAndUnfocused,
      underlineButtonState: Constants.formatButtonState.disabledAndUnfocused,
      alignLeftButtonState: Constants.formatButtonState.disabledAndUnfocused,
      alignRightButtonState: Constants.formatButtonState.disabledAndUnfocused,
      alignCenterButtonState: Constants.formatButtonState.disabledAndUnfocused,
      alignJustifyButtonState: Constants.formatButtonState.disabledAndUnfocused,
      listButtonState: Constants.formatButtonState.disabledAndUnfocused,
      numberedListButtonState: Constants.formatButtonState.disabledAndUnfocused,
      fontSizeButtonState: Constants.formatButtonState.disabledAndUnfocused,
      fontFamilyButtonState: Constants.formatButtonState.disabledAndUnfocused,
    }));

    handleActionMenuBar(args);
  };

  const getChangedAttachments = () => {
    let clonedDxmlAttachments = Array.isArray(
      editorAttachmentRef.current?.Attachments,
    )
      ? _.cloneDeep(editorAttachmentRef.current.Attachments)
      : [];
    clonedDxmlAttachments = clonedDxmlAttachments.filter(
      (x) => x.Action === Constants.attachmentActionType.createOrUpdate,
    );
    clonedDxmlAttachments = clonedDxmlAttachments.concat(
      _.cloneDeep(attachmentsToDeleteRef.current.filter((x) => x.Id)),
    );

    editorAttachmentRef.current.Attachments?.forEach((dxmlAttachment) => {
      dxmlAttachment.Action = Constants.attachmentActionType.none;
    });

    return clonedDxmlAttachments;
  };

  const updateIdAndRemoveDeletedAttachment = (attachments) => {
    // update id
    editorAttachmentRef.current.Attachments?.forEach((dxmlAttachment) => {
      updateDxmlAttachmentId(attachments, dxmlAttachment);
    });

    // update id
    attachmentsToDeleteRef.current.forEach((dxmlAttachment) => {
      updateDxmlAttachmentId(attachments, dxmlAttachment);
    });

    // remove deleted attachments from attachmentsToDeleteRef
    attachments
      ?.filter(
        (dxmlAttachment) =>
          dxmlAttachment.Action === Constants.attachmentActionType.delete,
      )
      .forEach((dxmlAttachment) => {
        const index = attachmentsToDeleteRef.current.findIndex(
          (x) => x.Id === dxmlAttachment.Id,
        );
        if (index >= 0) {
          attachmentsToDeleteRef.current.splice(index, 1);
        }
      });
  };

  const updateDxmlAttachmentId = (attachments, dxmlAttachment) => {
    if (!dxmlAttachment.Id) {
      const found = attachments?.find(
        (x) =>
          x.Uuid === dxmlAttachment.Uuid &&
          x.Action === Constants.attachmentActionType.createOrUpdate,
      );
      if (found) {
        dxmlAttachment.Id = found.Id;
      }
    }
  };

  const saveEditorFile = async (args) => {
    setSaving(true);
    const newEditorFile = _.cloneDeep(args.editorFile);
    newEditorFile.ModifiedTime = editorFileRef.current.ModifiedTime;
    if (args.distribution) {
      newEditorFile.Distribution = args.distribution;
    }
    newEditorFile.EditorInputFields.forEach((editorInputField) => {
      if (
        editorInputField.InputFieldType === Constants.inputFieldType.checkbox
      ) {
        if (args.values[editorInputField.Id]) {
          if (editorInputField.DxmlAttributes.CheckVal) {
            editorInputField.Value = editorInputField.DxmlAttributes.CheckVal;
          }
        } else {
          if (editorInputField.DxmlAttributes.UncheckVal) {
            editorInputField.Value = editorInputField.DxmlAttributes.UncheckVal;
          }
        }
      } else {
        editorInputField.Value = args.values[editorInputField.Id];
      }
    });

    newEditorFile.Attachments = getChangedAttachments();

    let res;
    try {
      // another undo ? state here

      undoRedoManager.saveState({
        editorFile: newEditorFile,
        timestamp: Date.now(),
        source: 'saveEditorFile'
      });

      newEditorFile.sessionId = sessionId;
      res = await updateEditorFile(
        newEditorFile,
        startParameter,
        themeAndStyle?.showProgressBar,
      );

      if (res) {
        editorFileRef.current.ModifiedTime = res.ModifiedTime;
        editorFileRef.current.EditorInputFields = res.EditorInputFields;
        setModifiedDate(res?.ModifiedTime);

        updateIdAndRemoveDeletedAttachment(res?.Attachments);

        handleUpdateResult(res);

        setPreviewRefreshDisabled(false);

        // Button states will be updated by the useEffect when undo/redo state changes
      }

      if (saveHasFailed) {
        successAfterFailedToSaveHandler();
        setSaveHasFailed(false);
      }

      setSaving(false);
    } catch (err) {
      setSaving(false);

      setSaveHasFailed(true);

      if (
        err.response?.data?.Message.startsWith("(030)") ||
        err.response?.data?.Message.startsWith("(031)")
      ) {
        const message = err.response?.data?.Message;
        toastHandler(message, "error");
        if (editorFileRef.current.DxmlFileStatus === Constants.fileStatus.ok) {
          editorFileRef.current.DxmlFileStatus = Constants.fileStatus.draft;
          unlockEditor();;
          return;
        }
      } else {
        toastHandler(terr(translations.errors.failSaveDocument), "error");
      }
    }

    return res;
  };

  const handlePreviewHtml = async () => {
    setPreviewType(Constants.previewType.html);
    setPreviewRefreshDisabled(true);
    setForcePreviewLoad(prev => prev + 1);
    switch (actionMenuBar.splitScreenState) {
      case Constants.screenState.splitScreen:
        break;
      default:
        loadAction.execute(async () => {
          const res = await previewHtml(startParameter);
          const blob = new Blob([res], { type: "text/html" });
          const newBlobUrl = URL.createObjectURL(blob);
          const htmlWindow = window.open();
          htmlWindow.location.href = newBlobUrl;
        }, terr(translations.errors.failLoadFile));
        break;
    }
  };

  const handlePreviewPdf = async () => {
    setPreviewType(Constants.previewType.pdf);
    setPreviewRefreshDisabled(true);
    setForcePreviewLoad(prev => prev + 1);
    switch (actionMenuBar.splitScreenState) {
      case Constants.screenState.splitScreen:
        break;
      default:
        loadAction.execute(async () => {
          const res = await previewPdf(startParameter);
          const blob = new Blob([res], { type: "application/pdf" });
          const newBlobUrl = URL.createObjectURL(blob);
          const pdfWindow = window.open();
          pdfWindow.location.href = newBlobUrl;
          // setBlobUrl(newBlobUrl);
        }, terr(translations.errors.failLoadFile));
        break;
    }
  };

  const handleLocalPrintViewpointPdf = async () => {
    try {
      await loadAction.execute(async () => {
        const res = await getViewpointPdf(startParameter);
        const blob = new Blob([res], { type: "application/pdf" });
        const newBlobUrl = URL.createObjectURL(blob);

        if (config.usePdfPreviewModal) {
          setPdfPreviewModal({
            open: true,
            blobUrl: newBlobUrl,
            title: "Local Print",
          });
        } else {
          const pdfWindow = window.open();
          pdfWindow.location.href = newBlobUrl;
        }
      }, terr(translations.errors.failLoadFile));
    } finally {
      // Hide loading after PDF dialog shows or if error occurs
      setLocalPrintLoading(false);
    }
  };

  const handleLocalPrintDynamoPdf = async (jobId, docId) => {
    try {
      await loadAction.execute(async () => {
        const res = await getDynamoPdf(startParameter, jobId, docId);
        const blob = new Blob([res], { type: "application/pdf" });
        const newBlobUrl = URL.createObjectURL(blob);

        if (config.usePdfPreviewModal) {
          setPdfPreviewModal({
            open: true,
            blobUrl: newBlobUrl,
            title: "Local Print",
          });
        } else {
          const pdfWindow = window.open();
          pdfWindow.location.href = newBlobUrl;
        }
      }, terr(translations.errors.failLoadFile));
    } finally {
      // Hide loading after PDF dialog shows or if error occurs
      setLocalPrintLoading(false);
    }
  };

  const handleUpdateResult = (updateResult) => {
    handlePrePostResult(updateResult);
    handlePublishResult(updateResult);
    handleDiscardResult(updateResult);
  };

  const handlePublishResult = (updateResult) => {
    if (updateResult.DxmlFileStatus !== Constants.fileStatus.ok) {
      return;
    }

    if (publish) {
      publish();
    }

    if (updateResult.WebEditorSettings?.EndFormEnabled === false) {
      toastHandler(
        te(translations.edit.distribution.successDocumentPublishment),
        "success",
      );
    } else {
      toastHandler(
        te(translations.edit.distribution.successDocumentDistribution),
        "success",
      );

      if (updateResult.Distribution === Constants.distribution.localPrint) {
        if (
          updateResult.WebEditorSettings?.ArchiveMethod ===
          Constants.distributionArchiveMethod.viewpoint
        ) {
          handleLocalPrintViewpointPdf();
          if (!config.usePdfPreviewModal) {
            document
              .getElementById(`${Constants.localPrintViewpointPdfLinkId}`)
              .click();
          }
        } else if (
          updateResult.WebEditorSettings?.ArchiveMethod ===
          Constants.distributionArchiveMethod.onprem
        ) {
          handleLocalPrintDynamoPdf(
            updateResult.DynamoJobId,
            updateResult.DynamoDocId,
          );
          // const win = window.open(`/WebEditor/Preview/Dynamo/Pdf/${startParameter}/${updateResult.DynamoJobId}-${updateResult.DynamoDocId}.pdf` + (languageCodeFromQueryString ? `?lng=${languageCodeFromQueryString}` : ''), '_blank');
          // if (win != null) {
          //     win.focus();
          // }
        }
      }
    }
  };

  const handleDiscardResult = (updateResult) => {
    if (updateResult.DxmlFileStatus !== Constants.fileStatus.discarded) {
      return;
    }

    if (discard) {
      discard();
    }
  };

  const { debouncedSave: autoSaveEditorFile, flushSaves } = useAutoSave((args) => saveEditorFile(args), 1000);

  // Undo/Redo handlers
  const handleUndo = useCallback(async () => {
    const previousState = undoRedoManager.undo();
    if (previousState && previousState.editorFile) {
      try {
        // Lock the editor during undo operation
        setUndoRedoLoading(true);

        // Update the previous state with current ModifiedTime before saving
        const stateToRestore = {
          ...previousState.editorFile,
          ModifiedTime: editorFileRef.current.ModifiedTime,
        };

        stateToRestore.sessionId = sessionId;
        const res = await updateEditorFile(
          stateToRestore,
          startParameter,
          themeAndStyle?.showProgressBar,
        );

        if (res) {
          editorFileRef.current = {
            ...stateToRestore,
            ModifiedTime: res.ModifiedTime,
          };
          setModifiedDate(res?.ModifiedTime);

          setPreviewRefreshDisabled(false);

          // Update form data and UI state to match restored editor file
          handleData({
            editorFile: editorFileRef.current,
            actionMenuBar: {
              selectableTextState: Constants.normalState,
              textLibraryState: Constants.normalState,
              previewPdfState: Constants.normalState,
              previewHtmlState: Constants.normalState,
              publishState: Constants.normalState,
              discardState: Constants.normalState,
            },
          });

          // Button states will be updated by the useEffect
          toastHandler("Undo completed", "success");
        }
      } catch (err) {
        toastHandler("Failed to undo", "error");
        console.error("Undo error:", err);
      } finally {
        // Unlock the editor
        setUndoRedoLoading(false);
      }
    }
  }, [undoRedoManager, startParameter, themeAndStyle, toastHandler]);

  const handleRedo = useCallback(async () => {
    const nextState = undoRedoManager.redo();
    if (nextState && nextState.editorFile) {
      try {
        // Lock the editor during redo operation
        setUndoRedoLoading(true);

        // Update the next state with current ModifiedTime before saving
        const stateToRestore = {
          ...nextState.editorFile,
          ModifiedTime: editorFileRef.current.ModifiedTime,
        };

        stateToRestore.sessionId = sessionId;
        const res = await updateEditorFile(
          stateToRestore,
          startParameter,
          themeAndStyle?.showProgressBar,
        );

        if (res) {
          editorFileRef.current = {
            ...stateToRestore,
            ModifiedTime: res.ModifiedTime,
          };
          setModifiedDate(res?.ModifiedTime);

          setPreviewRefreshDisabled(false);

          // Update form data and UI state to match restored editor file
          handleData({
            editorFile: editorFileRef.current,
            actionMenuBar: {
              selectableTextState: Constants.normalState,
              textLibraryState: Constants.normalState,
              previewPdfState: Constants.normalState,
              previewHtmlState: Constants.normalState,
              publishState: Constants.normalState,
              discardState: Constants.normalState,
            },
          });

          // Button states will be updated by the useEffect
          toastHandler("Redo completed", "success");
        }
      } catch (err) {
        toastHandler("Failed to redo", "error");
        console.error("Redo error:", err);
      } finally {
        // Unlock the editor
        setUndoRedoLoading(false);
      }
    }
  }, [undoRedoManager, startParameter, themeAndStyle, toastHandler]);

  // Event listeners for undo/redo
  useEffect(() => {
    const handleUndoEvent = () => {
      if (!undoRedoLoading) {
        handleUndo();
      }
    };
    const handleRedoEvent = () => {
      if (!undoRedoLoading) {
        handleRedo();
      }
    };

    window.addEventListener("webeditor-undo", handleUndoEvent);
    window.addEventListener("webeditor-redo", handleRedoEvent);

    return () => {
      window.removeEventListener("webeditor-undo", handleUndoEvent);
      window.removeEventListener("webeditor-redo", handleRedoEvent);
    };
  }, [handleUndo, handleRedo, undoRedoLoading]);

  // Keyboard shortcuts for undo/redo
  useHotkeys(
    "ctrl+z",
    () => {
      if (!undoRedoLoading) {
        handleUndo();
      }
    },
    [handleUndo, undoRedoLoading],
  );
  useHotkeys(
    "ctrl+y",
    () => {
      if (!undoRedoLoading) {
        handleRedo();
      }
    },
    [handleRedo, undoRedoLoading],
  );

  // Update undo/redo button states when the stack changes
  useEffect(() => {
    // Debounce button state updates to prevent excessive re-renders
    const timeoutId = setTimeout(() => {
      updateUndoRedoButtonStates();
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [
    updateUndoRedoButtonStates,
    undoRedoManager.canUndo,
    undoRedoManager.canRedo,
    undoRedoLoading,
  ]);

  // // Wrapper for form field changes that also saves undo/redo state
  const handleInputFieldChangeWithUndoRedo = useCallback(
    (originalHandleChange) => {
      return (event) => {
        // Call the original handleChange first
        originalHandleChange(event);

        // Save state AFTER form field change (with new value)
        // setTimeout(() => {
        //   if (undoRedoManager && editorFileRef.current) {
        //     undoRedoManager.saveState({
        //       editorFile: _.cloneDeep(editorFileRef.current),
        //       timestamp: Date.now(),
        //       source: 'handleInputFieldChangeWithUndoRedo'
        //     });

        //     // Button states will be updated by the useEffect
        //   }
        // }, 50); // Small delay to ensure form state is updated
      };
    },
    [undoRedoManager],
  );

  const onPreviewPdfClick = async (values) => {
    handleActionMenuBar({
      actionMenuBar: {
        selectableTextState: Constants.normalState,
        textLibraryState: Constants.normalState,
        previewPdfState: Constants.focusedState,
        previewHtmlState: Constants.normalState,
        publishState: Constants.normalState,
        discardState: Constants.normalState,
      },
      editorFile: _.cloneDeep(editorFileRef.current),
    });
    // Wait for all queued saves to complete
    await flushSaves();
    handlePreviewPdf();
  };

  const onPreviewHtmlClick = async (values) => {
    handleActionMenuBar({
      actionMenuBar: {
        selectableTextState: Constants.normalState,
        textLibraryState: Constants.normalState,
        previewPdfState: Constants.normalState,
        previewHtmlState: Constants.focusedState,
        publishState: Constants.normalState,
        discardState: Constants.normalState,
      },
      editorFile: _.cloneDeep(editorFileRef.current),
    });
    // Wait for all queued saves to complete
    await flushSaves();
    handlePreviewHtml();
  };

  const handlePublishClicked = async () => {
    lockEditor();

    const processModel = await getProcesses(startParameter);

    if (processModel?.PrePostProcessWorkflowId) {
      handlePrePostProcess(processModel.PrePostProcessWorkflowId);
    } else {
      publishDocument();
    }
  };

  const handlePrePostProcess = async (prePostProcessWorkflowId) => {
    lockEditor();

    autoSaveEditorFile({
      editorFile: _.cloneDeep({
        ...editorFileRef.current,
        prePostProcessWorkflowId: prePostProcessWorkflowId,
      }),
      values: _.cloneDeep(formikValuesRef.current),
    });
  };

  const handlePrePostResult = (updateResult) => {
    editorFileRef.current.prePostProcessWorkflowId = null;

    if (!updateResult?.PrePostProcessResult) return;

    if (updateResult?.PrePostProcessResult?.Result === false) {
      unlockEditor();
      toastHandler(updateResult?.PrePostProcessResult?.Message, "error");
      return;
    }

    publishDocument();
  };

  const publishDocument = async () => {
    lockEditor();

    if (publish) {
      handleUserDefinedPublish();
    } else {
      handlePublish();
    }
  };

  const handleUserDefinedPublish = () => {
    if (editorFileRef.current.WebEditorSettings?.EndFormEnabled) {
      toastHandler(terr(translations.errors.enableDistributionError), "error");
      return;
    }

    editorFileRef.current.DxmlFileStatus = Constants.fileStatus.ok;

    lockEditor();

    autoSaveEditorFile({
      editorFile: _.cloneDeep(editorFileRef.current),
      values: _.cloneDeep(formikValuesRef.current),
    });
  };

  const handlePublish = async () => {
    lockEditor();
    const distributionList = await getDistributionList(startParameter);

    if (distributionList) {
      handleDistributionList(distributionList);
      return;
    }

    const dialogOpen = checkDistributionMethodsAndDialogOpen();

    setDistributionDialogOpen(dialogOpen);

    // It uses distribution method in the editor settings
    // Distribution index in DXML is not NotSet
    if (!dialogOpen) {
      distributeDocument(editorFileRef.current.Distribution);
    }
  }


  const handleDistributionList = (distributionList) => {
    if (!distributionList)
      return;

    if (distributionList.Status !== Constants.distributionListsStatus.active) {
      toastHandler(terr(translations.errors.inactiveDistributionList), 'error');
      return;
    }

    const activeDistributionAlternatives = distributionList.DistributionAlternatives?.filter(x => x.Status === Constants.distributionListsStatus.active);
    if (!activeDistributionAlternatives || activeDistributionAlternatives.length === 0) {
      toastHandler(terr(translations.errors.noActiveDistributionAlternatives), 'error');
      return;
    }

    const newDistributionOptions = activeDistributionAlternatives.map(x => { return { name: x.Name, value: x.Id, selected: false, description: x.Description, distributionAlternative: x } });
    if (newDistributionOptions.length > 0) {
      newDistributionOptions[0].selected = true;
      editorFileRef.current.DistributionAlternative = newDistributionOptions[0].distributionAlternative;
    }

    setDistributionOptions(newDistributionOptions);
    setDistributionDialogOpen(true);
  }

  const checkDistributionMethodsAndDialogOpen = () => {
    let newDistributionOptions = [];

    if (editorFileRef.current.Distribution === Constants.distribution.notSet) {
      if (editorFileRef.current.WebEditorSettings.CentralDistributionEnabled) {
        newDistributionOptions.push({
          name: te(translations.edit.distribution.centralDistribution),
          value: Constants.distribution.notSet,
          selected: false,
        });
      }
      if (editorFileRef.current.WebEditorSettings.LocalPrintEnabled) {
        newDistributionOptions.push({
          name: te(translations.edit.distribution.localPrint),
          value: Constants.distribution.localPrint,
          selected: false,
        });
      }
      if (editorFileRef.current.WebEditorSettings.EmailEnabled) {
        newDistributionOptions.push({
          name: te(translations.edit.distribution.email),
          value: Constants.distribution.email,
          selected: false,
        });
      }
      if (editorFileRef.current.WebEditorSettings.ArchiveOnlyEnabled) {
        newDistributionOptions.push({
          name: te(translations.edit.distribution.archiveOnly),
          value: Constants.distribution.archiving,
          selected: false,
        });
      }
      if (newDistributionOptions.length > 0) {
        newDistributionOptions[0].selected = true;
      }
    }

    setDistributionOptions(newDistributionOptions);

    return newDistributionOptions.length > 0 ? true : false;
  };

  const distributeDocument = async (distribution, distributionAlternative) => {
    lockEditor();

    editorFileRef.current.DxmlFileStatus = Constants.fileStatus.ok;

    if (distributionAlternative) {
      editorFileRef.current.DistributionAlternative = distributionAlternative;
      distribution = Constants.distribution.notSet;
    }

    // Show loading for local print
    if (distribution === Constants.distribution.localPrint) {
      setLocalPrintLoading(true);
    }

    autoSaveEditorFile({
      editorFile: _.cloneDeep(editorFileRef.current),
      values: _.cloneDeep(formikValuesRef.current),
      distribution: distribution,
    });
  };

  const onDiscardClick = () => {
    setDiscardDialogOpen(true);
  };

  const discardDocument = async () => {
    editorFileRef.current.DxmlFileStatus = Constants.fileStatus.discarded;

    lockEditor();

    autoSaveEditorFile({
      editorFile: _.cloneDeep(editorFileRef.current),
      values: _.cloneDeep(formikValuesRef.current),
    });
  };

  const lockEditor = () => {
    setEditorDisabled(true);

    selectedTextPartIdRef.current = null;

    setSelectedEditorInsertPoint(null);

    setSideBarState(Constants.sideBarState.closed);

    setFormatMenuBar((preState) => ({
      ...preState,
      boldButtonState: Constants.formatButtonState.disabledAndUnfocused,
      italicButtonState: Constants.formatButtonState.disabledAndUnfocused,
      underlineButtonState: Constants.formatButtonState.disabledAndUnfocused,
      alignLeftButtonState: Constants.formatButtonState.disabledAndUnfocused,
      alignRightButtonState: Constants.formatButtonState.disabledAndUnfocused,
      alignCenterButtonState: Constants.formatButtonState.disabledAndUnfocused,
      alignJustifyButtonState: Constants.formatButtonState.disabledAndUnfocused,
      listButtonState: Constants.formatButtonState.disabledAndUnfocused,
      numberedListButtonState: Constants.formatButtonState.disabledAndUnfocused,
      fontSizeButtonState: Constants.formatButtonState.disabledAndUnfocused,
      fontFamilyButtonState: Constants.formatButtonState.disabledAndUnfocused,
    }));

    setActionMenuBar({
      selectableTextState: Constants.disabledState,
      textLibraryState: Constants.disabledState,
      previewPdfState: Constants.disabledState,
      previewHtmlState: Constants.disabledState,
      splitScreenState: Constants.disabledState,
      publishState: Constants.disabledState,
      discardState: Constants.disabledState,
    });
  };

  const unlockEditor = () => {
    setEditorDisabled(false);

    handleActionMenuBar({
      actionMenuBar: {
        selectableTextState: Constants.normalState,
        textLibraryState: Constants.normalState,
        previewPdfState: Constants.normalState,
        previewHtmlState: Constants.normalState,
        splitScreenState: Constants.screenState.singleScreen,
        publishState: Constants.normalState,
        discardState: Constants.normalState,
      },
      editorFile: _.cloneDeep(editorFileRef.current),
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  const handleBackClick = async () => {
    const smartformRedirectUrl = await reloadSmartform(startParameter);
    if (smartformRedirectUrl) {
      window.location.href = smartformRedirectUrl;
    }
  };

  return (
    <>
      <Formik
        validateOnChange={true}
        validateOnBlur={true}
        initialValues={formikInitialValues}
        enableReinitialize={true}
        validationSchema={yup.object().shape(formikValidationSchema.current)}
        onSubmit={async (values, { setSubmitting }) => {
          setSubmitting(false);
        }}
      >
        {({
          values,
          errors,
          handleChange,
          validateForm,
          isSubmitting,
          resetForm,
          setValues,
          /* and other goodies */
        }) => {
          // Store Formik methods for undo/redo operations
          formikMethodsRef.current = { resetForm, setValues };

          return (
            <WebEditorContext.Provider
              value={{
                undoRedoManager: undoRedoManager,
                editorDisabled: editorDisabled || undoRedoLoading,
                isLoading: loadAction.isExecuting || undoRedoLoading,
                editorFileRef: editorFileRef,
                editorAttachment: editorAttachment,
                setEditorAttachment: setEditorAttachment,
                attachmentsToDeleteRef: attachmentsToDeleteRef,
                lastUsedQuillRef: lastUsedQuillRef,
                lastUsedQuillIndexRef: lastUsedQuillIndexRef,
                quillRef: quillRef,
                languageCodeFromQueryString: languageCodeFromQueryString,
                formatMenuBar: formatMenuBar,
                setFormatMenuBar: setFormatMenuBar,
                actionMenuBar: actionMenuBar,
                handleActionMenuBar: handleActionMenuBar,
                sideBarState: sideBarState,
                searchClickedCount,
                setSearchClickedCount,
                lastUsedQuillId,
                setLastUsedQuillId,
                setSideBarState: setSideBarState,
                formikValuesRef: formikValuesRef,
                formikValues: values,
                inputFieldErrors: errors,
                onInputFieldChange: handleInputFieldChangeWithUndoRedo(handleChange),
                inputFieldTouchedRef: inputFieldTouchedRef,
                currentLanguage: currentLanguage,
                undoRedoManager: undoRedoManager,
                handleUndo: handleUndo,
                handleRedo: handleRedo,
                undoRedoLoading: undoRedoLoading,
                saving: saving,
                setActionMenuBar: setActionMenuBar,
                autoSaveEditorFile: async (args) => {
                  const newErrors = await validateForm(formikValuesRef.current);
                  const hasTouchedErrors = Object.keys(newErrors).find(
                    (key) =>
                      !_.isEmpty(inputFieldTouchedRef.current) &&
                      inputFieldTouchedRef.current[key] === true,
                  );
                  if (_.isEmpty(newErrors) || !hasTouchedErrors) {
                    editorFileRef.current.DxmlFileStatus =
                      Constants.fileStatus.draft;

                    autoSaveEditorFile({
                      editorFile: _.cloneDeep(editorFileRef.current),
                      values: _.cloneDeep(formikValuesRef.current),
                    });
                  }
                },
                autoSaveAttachment: async () => {
                  editorFileRef.current.DxmlFileStatus =
                    Constants.fileStatus.draft;

                  autoSaveEditorFile({
                    editorFile: _.cloneDeep(editorFileRef.current),
                    values: _.cloneDeep(formikValuesRef.current),
                  });
                },
                onPreviewPdfClick: async () => {
                  onPreviewPdfClick();
                },
                onPreviewHtmlClick: async () => {
                  onPreviewHtmlClick();
                },
                onPublishClick: async () => {
                  inputFieldTouchedRef.current = {};
                  Object.keys(formikValuesRef.current).forEach(key => {
                    inputFieldTouchedRef.current[key] = true;
                  })
                  const newErrors = await validateForm(formikValuesRef.current);
                  if (_.isEmpty(newErrors)) {
                    handlePublishClicked();
                  } else {
                    toastHandler(terr(translations.errors.publishErrorMsg), "error");
                  }
                },
                onDiscardClick: async () => {
                  onDiscardClick();
                },
                previewType: previewType,
                previewRefreshDisabled: previewRefreshDisabled,
                setPreviewRefreshDisabled: setPreviewRefreshDisabled,
                forcePreviewLoad: forcePreviewLoad,
                setForcePreviewLoad: setForcePreviewLoad,
                rightClickMenu: rightClickMenu,
                setRightClickMenu: setRightClickMenu,
                unlockEditor: unlockEditor,
              }}>
              <SelectableTextContext.Provider value={{
                keywords: keywords,
                selectableTexts: selectableTexts,
                selectedTextPartIdRef: selectedTextPartIdRef,
                hoveredSelectableText: hoveredSelectableText,
                setHoveredSelectableText: setHoveredSelectableText,
              }}>
                <TextLibraryContext.Provider value={{
                  selectedEditorInsertPoint: selectedEditorInsertPoint,
                  setSelectedEditorInsertPoint: setSelectedEditorInsertPoint,
                  textLibraryDialogOpen: textLibraryDialogOpen,
                  setTextLibraryDialogOpen: setTextLibraryDialogOpen,
                  setQuillBeforeTextLibraryDialogOpen: setQuillBeforeTextLibraryDialogOpen,
                  setTemplateTextValueSelectedInDialog: setTemplateTextValueSelectedInDialog,
                }}>
                  <DistributionContext.Provider value={{
                    distributionDialogOpen: distributionDialogOpen,
                    setDistributionDialogOpen: setDistributionDialogOpen,
                    distributionOptions: distributionOptions,
                    distributeDocument: async (distribution, distributionAlternative) => {
                      distributeDocument(distribution, distributionAlternative);
                    },
                  }}>
                    <DiscardContext.Provider value={{
                      discardDialogOpen: discardDialogOpen,
                      setDiscardDialogOpen: setDiscardDialogOpen,
                      discardDocument: async () => {
                        discardDocument();
                      }
                    }}>
                      <form id="webEditorForm" onKeyDown={handleKeyDown}>
                        <>
                          <div className="tw-relative tw-flex tw-flex-col tw-bg-white tw-h-screen">
                            <header className="tw-sticky tw-top-0 tw-z-10 tw-w-full tw-bg-white tw-shadow-sm">
                              {/* {
                                                            !themeAndStyle?.hideTopBar &&
                                                            <div className="tw-w-full tw-bg-indigo-700 ">
                                                                <div className="tw-max-w-screen-2xl tw-mx-auto tw-px-4 sm:tw-px-6">
                                                                    <div className="tw-flex tw-justify-between tw-h-12">
                                                                        <div className="tw-flex tw-px-2 lg:tw-px-0">
                                                                            <div className="tw-flex-shrink-0 tw-flex tw-items-center">
                                                                                <span className="tw-ml-4 tw-text-white tw-text-sm tw-font-medium">WebEditor</span>
                                                                            </div>
                                                                        </div>
                                                                        <div className="tw-flex-1 tw-flex tw-items-center tw-justify-center tw-px-2 lg:tw-ml-6 lg:tw-justify-end">
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        } */}

                              {!themeAndStyle?.hideTopBar && (
                                <div
                                  className={`tw-border-b ${themeAndStyle?.hideContextMenu ? "tw-py-4" : ""} ${saving ? "tw-bg-gray-100" : ""}`}
                                >
                                  <div className="tw-flex tw-justify-between tw-max-w-screen-3xl tw-mx-auto tw-px-4 sm:tw-px-6">
                                    <div className="tw-flex-shrink-0 tw-flex tw-items-center">
                                      {showBackButton && (
                                        <button
                                          type="button"
                                          aria-label={tc(
                                            translations.common.backToSmartform,
                                          )}
                                          className="button-tooltip-container tw-px-2 tw-py-1 tw-bg-white tw-flex tw-items-center tw-text-gray-500 tw-border tw-rounded-md
                                                                                hover:tw-text-gray-600 hover:tw-bg-gray-50 focus:tw-outline-none focus:tw-border-indigo-600 focus:tw-ring-indigo-600 focus:tw-ring-2
                                                                                disabled:tw-bg-gray-200 disabled:tw-text-gray-400 disabled:tw-cursor-not-allowed disabled:tw-border-gray-300"
                                          style={{ marginLeft: "-1.75rem" }}
                                          disabled={editorDisabled}
                                          onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                              e.preventDefault();
                                              e.target.click();
                                            }
                                          }}
                                          onClick={handleBackClick}
                                        >
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="tw-h-5 tw-w-6"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          >
                                            <path d="M18 15h-6v4l-7-7 7-7v4h6v6z"></path>
                                          </svg>
                                          <span
                                            role="tooltip"
                                            id="preview-tooltip-desc"
                                            className="button-tooltip"
                                          >
                                            {tc(
                                              translations.common
                                                .backToSmartform,
                                            )}
                                          </span>
                                        </button>
                                      )}
                                      <span className="tw-ml-4 tw-text-sm tw-font-medium">
                                        Editor
                                      </span>
                                    </div>
                                    {!themeAndStyle?.hideContextMenu && (
                                      <div className="tw-mr-4">
                                        <div className="tw-py-3">
                                          <nav
                                            aria-label="Global"
                                            className="tw-ml-6 tw-flex tw-items-center tw-gap-x-2 sm:tw-gap-x-4"
                                          >
                                            <a
                                              href={
                                                `/WebEditor/${startParameter}` +
                                                (languageCodeFromQueryString
                                                  ? `?lng=${languageCodeFromQueryString}`
                                                  : "")
                                              }
                                              className={`${nav === Constants.menuState.edit ? "tw-bg-gray-100 tw-text-gray-900" : "tw-text-gray-900 hover:tw-bg-gray-50 hover:tw-text-gray-900"} tw-block tw-rounded-md tw-py-1 tw-px-3 tw-text-sm tw-font-medium `}
                                              onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                  e.preventDefault();
                                                  e.target.click();
                                                }
                                              }}
                                            >
                                              {tc(translations.common.edit)}
                                            </a>
                                            <a
                                              href={
                                                `/WebEditor/Attachments/${startParameter}` +
                                                (languageCodeFromQueryString
                                                  ? `?lng=${languageCodeFromQueryString}`
                                                  : "")
                                              }
                                              className={`${nav === Constants.menuState.attachment ? "tw-bg-gray-100 tw-text-gray-900" : "tw-text-gray-900 hover:tw-bg-gray-50 hover:tw-text-gray-900"} tw-block tw-rounded-md tw-py-1 tw-px-3 tw-text-sm tw-font-medium `}
                                              onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                  e.preventDefault();
                                                  e.target.click();
                                                }
                                              }}
                                            >
                                              {tc(
                                                translations.common.attachment,
                                              )}{" "}
                                              {editorAttachment?.Attachments
                                                ?.length > 0
                                                ? ` (${editorAttachment.Attachments.length})`
                                                : ""}
                                            </a>
                                          </nav>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}

                              <div className="tw-max-w-screen-2xl tw-mx-auto  tw-mx-auto tw-px-4 sm:tw-px-6">
                                <div className={"tw-py-3"}>
                                  <nav className="tw-flex" aria-label="Breadcrumb">
                                    <div className="tw-block tw-w-full">
                                      <WebEditorMenuBar />
                                    </div>
                                  </nav>
                                </div>
                              </div>
                            </header>
                            <main className="tw-main-container tw-bg-paper-background tw-z-0 focus:tw-outline-none tw-h-full tw-overflow-auto">
                              <div className={`${actionMenuBar.splitScreenState === Constants.screenState.splitScreen ? "" : "tw-max-w-screen-3xl"} tw-h-full tw-mx-auto tw-inset-0 ${themeAndStyle.removeMarginAndPadding ? '' : 'tw-py-6 tw-px-4 sm:tw-px-6 lg:tw-px-8'} tw-bg-paper-background`}>
                                <div className={`tw-flex tw-flex-col tw-h-full ${themeAndStyle.removeMarginAndPadding ? '' : 'tw-pb-10'}`}>
                                  <SplitView
                                    id="editorAndSizebarSplitView"
                                    hideRightPane={sideBarState === Constants.sideBarState.closed || !window.matchMedia("(min-width: 1024px)").matches}
                                    useRatio={false}
                                  >
                                    <SplitView
                                      id="editorAndPreviewSplitView"
                                      hideLeftPane={actionMenuBar.splitScreenState !== Constants.screenState.splitScreen}
                                    >
                                      {/* Preview View */}
                                      <PreviewView />

                                      {/* Editor View */}
                                      <div className="tw-h-full tw-flex tw-flex-col">
                                        <SectionHeader>
                                          {actionMenuBar.splitScreenState === Constants.screenState.splitScreen && (
                                            <span className="tw-text-sm tw-font-medium">Editor</span>
                                          )}
                                        </SectionHeader>
                                        <MandatoryFieldBanner nav={nav} />
                                        <main
                                          id={Constants.dxmlDocumentContainerId}
                                          className="tw-flex-1 tw-relative tw-z-999 tw-h-full tw-overflow-y-auto tw-border tw-shadow-2xl tw-rounded-sm tw-scroll-smooth tw-bg-white"
                                        >
                                          <div className="tw-absolute tw-inset-0 tw-h-full">
                                            {children}
                                          </div>
                                        </main>
                                      </div>
                                    </SplitView>

                                    {/* Sidebar */}
                                    <Transition.Root show={sideBarState !== Constants.sideBarState.closed} as={Fragment}>
                                      <Transition.Child
                                        as={Fragment}
                                        enter="tw-transform tw-transition tw-ease-in-out tw-duration-500 sm:tw-duration-700"
                                        enterFrom="tw-translate-x-full"
                                        enterTo="tw-translate-x-0"
                                        leave="tw-transform tw-transition tw-ease-in-out tw-duration-500 sm:tw-duration-700"
                                        leaveFrom="tw-translate-x-0"
                                        leaveTo="tw-translate-x-full"
                                      >
                                        <aside className="tw-hidden tw-h-full lg:tw-flex lg:tw-flex-col tw-flex-shrink-0">
                                          <SectionHeader />
                                          <div className="tw-h-full tw-border tw-shadow-xl tw-rounded-sm tw-bg-white tw-overflow-y-auto">
                                            {sideBarState === Constants.sideBarState.predefinedTexts && (
                                              <SelectableTextSideBar />
                                            )}
                                            {editorFileRef?.current?.WebEditorSettings?.TextLibraryEnabled &&
                                              sideBarState === Constants.sideBarState.textLibrary && (
                                                <TextLibrarySideBar />
                                              )}
                                          </div>
                                        </aside>
                                      </Transition.Child>
                                    </Transition.Root>
                                  </SplitView>
                                </div>
                              </div>
                            </main>
                          </div>

                          {sideBarState ===
                            Constants.sideBarState.predefinedTexts && (
                              <SelectableTextOverlapSideBar />
                            )}

                          {editorFileRef?.current?.WebEditorSettings
                            ?.TextLibraryEnabled &&
                            sideBarState ===
                            Constants.sideBarState.textLibrary && (
                              <div className="tw-h-full tw-w-full tw-flex lg:tw-hidden">
                                <TextLibraryOverlapSideBar />
                              </div>
                            )}
                          <DistributionDialog />
                          <DiscardDialog />
                          {textLibraryDialogOpen && <TextLibraryDialog />}

                          {loadAction.isExecuting && (
                            <div className="tw-fixed tw-inset-0 tw-flex tw-flex-col tw-justify-center align-center"></div>
                          )}

                          <a
                            id={Constants.localPrintViewpointPdfLinkId}
                            href={`/WebEditor/Preview/Viewpoint/Pdf/${startParameter}.pdf` + (languageCodeFromQueryString ? `?lng=${languageCodeFromQueryString}` : '')}
                            rel="noreferrer"
                            target="_blank"
                            className="tw-hidden"
                          >
                          </a>
                        </>
                      </form>
                    </DiscardContext.Provider>
                  </DistributionContext.Provider>
                </TextLibraryContext.Provider>
              </SelectableTextContext.Provider>
            </WebEditorContext.Provider>
          )
        }}
      </Formik>
      <GenericModal
        open={loginRequiredModalOpen}
        setOpen={setLoginRequiredModalOpen}
        title={authConfig?.reason === 401 ? tc(translations.common.authentication.authenticationRequired) : tc(translations.common.authentication.forbidden)}
        confirmButtonText={authConfig?.reason === 401 ? tc(translations.common.authentication.authenticate) : tc(translations.common.retry)}
        showCancelButton={true}
        cancelButtonText={authConfig?.reason === 401 ? tc(translations.common.cancel) : tc(translations.common.exit)}
        onConfirm={handleAuthenticate}
        onCancel={() => setLoginRequiredModalOpen(false)}
      >
        <div className="tw-py-8">
          {
            authConfig?.reason === 401 ? (

              <a>{tc(translations.common.authentication.authenticateMessage)}</a>

            ) : (
              <div>
                <p>{tc(translations.common.reason)}: <span className="tw-italic tw-font-semibold">{authConfig?.reasonMessage}</span></p>
                <p>{tc(translations.common.authentication.authFailedMessage)}</p>
              </div>
            )
          }
        </div>
      </GenericModal>
      <GenericModal
        open={getEditorFailedModalOpen}
        setOpen={setGetEditorFailedModalOpen}
        title={
          editorFailedModalData?.statusCode === 401 ?
            tc(translations.common.authentication.authenticationRequired) :
            editorFailedModalData?.statusCode === 403 ?
              tc(translations.common.authentication.forbidden) :
              loadAction.isExecuting ? 'Loading...' :
                ''
        }
        confirmButtonText={tc(translations.common.retry)}
        showCancelButton={true}
        cancelButtonText={tc(translations.common.cancel)}
        onConfirm={() => {
          setGetEditorFailedModalOpen(false);
          loadData();
        }}
        onCancel={() => {
          setGetEditorFailedModalOpen(false)
          setEditorFailedModalData(null)
        }}
      >
        <div className="tw-py-8">
          {
            authConfig?.reason === 401 ? (

              <a>{tc(translations.common.authentication.authenticateMessage)}</a>

            ) : (
              <div>
                <p>{tc(translations.common.reason)}: <span className="tw-italic tw-font-semibold">HTTP 401</span></p>
                <p>{tc(translations.common.authentication.authFailedMessage)}</p>
              </div>
            )
          }
        </div>
      </GenericModal>

      <PdfPreviewModal
        open={pdfPreviewModal.open}
        setOpen={(open) => setPdfPreviewModal(prev => ({ ...prev, open }))}
        blobUrl={pdfPreviewModal.blobUrl}
        title={pdfPreviewModal.title}
      />

      <RightClickMenuModal
        menu={rightClickMenu}
        setMenu={setRightClickMenu}
      >
      </RightClickMenuModal>

      <LoadingModal
        open={localPrintLoading}
      />

      {undoRedoLoading && (
        <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-50">
          <div className="tw-bg-white tw-rounded-lg tw-p-6 tw-shadow-xl tw-flex tw-items-center tw-space-x-3">
            <div className="tw-animate-spin tw-rounded-full tw-h-6 tw-w-6 tw-border-b-2 tw-border-indigo-600"></div>
            <span className="tw-text-gray-900 tw-font-medium">
              Processing undo/redo...
            </span>
          </div>
        </div>
      )}
    </>
  );
}
